import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Spinner } from '../../ui/Spinner';
import { useAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { btnPrimary, inputCls, labelCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
import { PASSWORD_HINT, PASSWORD_MAX, passwordProblem } from '../../core/rules/password';
import type { AccountOrder } from '../../core/types/account';
type Props = {
  email: string;
  firstName: string;
  orderId: string;
  order?: AccountOrder;
  subscribeIds?: string[];
  intervalWeeks?: number;
};
const ERROR_ID = 'after-password-error';
const HINT_ID = 'after-password-hint';
const ORDERS_PATH = '/account/bestellingen';
const benefits = ['Zien waar je pakket is', 'Een seintje als een product bijna op is', 'Later opnieuw bestellen in één tik'];

/**
 * De volgende stap na de betaling, als duidelijke kaart: ingelogd → naar je bestelling;
 * gast → één veld om een account te maken (de bestelling staat er dan al in).
 * Nooit vóór het betalen: afrekenen blijft altijd zonder account mogelijk.
 */
export function AccountAfterCheckout({
  email,
  firstName,
  orderId,
  order,
  subscribeIds,
  intervalWeeks
}: Props) {
  const {
    signedIn,
    register
  } = useAccount();
  const reduce = useReducedMotion();
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const weak = passwordProblem(password, {
      email,
      firstName
    });
    if (weak) {
      setError(weak);
      document.getElementById('after-password')?.focus();
      return;
    }
    setBusy(true);
    setError('');
    const r = await register({
      firstName,
      email,
      password,
      claim: order ? {
        order,
        subscribeIds: subscribeIds ?? [],
        intervalWeeks
      } : undefined
    });
    setBusy(false);
    if (r.ok) setDone(true);else setError(r.error ?? 'Dat lukte even niet. Probeer het nog eens.');
  };

  /** Bestelnummer en status: het tastbare bewijs dat je bestelling ergens "staat". */
  const orderStatus = (tone: 'dark' | 'light') => <p className={`inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[14px] ${tone === 'dark' ? 'text-shell/75' : 'text-muted'}`} data-id="element-5824" data-mp="1hy8x7iiz64rm">
      <span data-id="element-5825" data-mp="1mxlme9xbjnxd">
        Bestelling <span className={tone === 'dark' ? 'text-shell' : 'text-ink'} data-id="element-5826" data-mp="1mywng1zdqhks">{orderId}</span>
      </span>
      <span aria-hidden="true" data-id="element-5827" data-mp="1rxzufryx6xve">·</span>
      <span className="inline-flex items-center gap-1.5" data-id="element-5828" data-mp="1wvj7iojvt4lr">
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-clay" data-id="element-5829" data-mp="1wsx0qr4tet4l" />
        In behandeling
      </span>
    </p>;
  return <aside aria-labelledby="after-title" data-id="element-5830" data-mp="389n9oi0mhz1w">
      <AnimatePresence mode="wait" initial={false} data-id="element-5831" data-mp="388lmxga8vo4c">
        {signedIn || done ?
      // Het zwaarste blok op de pagina: donker vlak, één lichte knop. Hier ga je hierna heen.
      <motion.div key="account" role={done ? 'status' : undefined} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="bg-ink p-6 text-shell sm:p-8" data-id="element-5832" data-mp="rp1hbdmbkl11">
            {orderStatus('dark')}
            <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-5833" data-mp="8x8qnpuq3bwh">
              <div className="max-w-sm" data-id="element-5834" data-mp="3xw1gygdpsqq">
                <h2 id="after-title" className="font-display text-[1.75rem] font-light leading-tight" data-id="element-5835" data-mp="3yxcx41b4h8z">
                  {done ? 'Je account staat klaar, met je bestelling erin.' : 'Volg je bestelling in Mijn omgeving.'}
                </h2>
                <p className="mt-2 text-[15px] leading-relaxed text-shell/75" data-id="element-5836" data-mp="1y9swt2hxzir2">
                  Je status en gebruiksadvies op één plek. Zodra {founder.firstName} je pakket verstuurt, verschijnt daar ook de track & trace.
                </p>
              </div>
              <Link to={ORDERS_PATH} className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-shell px-7 py-4 text-[15px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-5837" data-mp="uzy84z2f4x5f">
                Bekijk je bestelling
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5838" data-mp="7adr4lpts9ys" />
              </Link>
            </div>
            <Link to="/account" className="mt-6 inline-flex py-1 text-[14px] text-shell/70 underline-offset-4 transition-colors duration-150 ease-soft hover:text-shell hover:underline" data-id="element-5839" data-mp="3ugd4sxb38way">
              Naar het overzicht van Mijn omgeving
            </Link>
          </motion.div> : <motion.form key="form" onSubmit={submit} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15,
        ease
      }} noValidate className="grid gap-8 bg-shell p-6 ring-1 ring-line sm:grid-cols-2 sm:p-8" data-id="element-5840" data-mp="3pg8k0v35dfex">
            <div data-id="element-5841" data-mp="3fu7d3jcrvr4v">
              {orderStatus('light')}
              <h2 id="after-title" className="mt-4 font-display text-[1.75rem] font-light leading-tight text-ink" data-id="element-5842" data-mp="3frl4dge5b5l2">
                Volg je bestelling in Mijn omgeving.
              </h2>
              <ul className="mt-4 space-y-2" data-id="element-5843" data-mp="3ossz5ohgdoae">
                {benefits.map(b => <li key={b} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-5844" data-mp="3krzbmw7xabi1">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.75} aria-hidden="true" data-id="element-5845" data-mp="2e9pg53faxa9" />
                    {b}
                  </li>)}
              </ul>
            </div>
            <div className="sm:border-l sm:border-line sm:pl-8" data-id="element-5846" data-mp="cd7d75t0yms7">
            <p className="text-[13px] text-muted" data-id="element-5847" data-mp="7exc6ekmcryo">
              Je bestelling staat er al in. Account voor <span className="text-ink" data-id="element-5848" data-mp="ei3wj8vc9i9o">{email}</span>
            </p>
            <label htmlFor="after-password" className={`${labelCls} mt-3`} data-id="element-5849" data-mp="jfw0505cistp">
              Kies een wachtwoord
            </label>
            <div className="relative" data-id="element-5850" data-mp="oe78t7jwmxac">
              <input id="after-password" maxLength={PASSWORD_MAX} type={show ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={e => {
              setPassword(e.target.value);
              if (error && e.target.value.length >= 8) setError('');
            }} aria-invalid={error ? true : undefined} aria-describedby={error ? `${HINT_ID} ${ERROR_ID}` : HINT_ID} className={`${inputCls} pr-12`} data-id="element-5851" data-mp="od5kw2hsigb7" />
              <button type="button" onClick={() => setShow(v => !v)} aria-label={show ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'} aria-pressed={show} className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5852" data-mp="38yxrioxgeuys">
                {show ? <EyeOffIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5853" data-mp="2wkra9s3emsyf" /> : <EyeIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5854" data-mp="2rp3mnh4km7v1" />}
              </button>
            </div>
            <p id={HINT_ID} className="mt-1.5 text-[13px] text-muted" data-id="element-5855" data-mp="3p9gc5tcab2b8">
              {PASSWORD_HINT}
            </p>
            {error && <p id={ERROR_ID} role="alert" className="mt-2 text-[13px] text-clay" data-id="element-5856" data-mp="3fctqx3g1q98o">
                {error}
              </p>}
            <button type="submit" disabled={busy} className={`${btnPrimary} mt-5 w-full py-4 text-[15px]`} data-id="element-5857" data-mp="3adql3gs8ryzn">
              {busy && <Spinner data-id="element-5858" data-mp="23w3ires2b7cf" />}
              {busy ? 'Je account wordt gemaakt…' : 'Maak account en volg je bestelling'}
            </button>
            <p className="mt-3 text-center text-[13px] text-muted" data-id="element-5859" data-mp="2ompllgt5ij0d">Liever niet? Prima, je bevestiging komt ook zo per mail.</p>
            </div>
          </motion.form>}
      </AnimatePresence>
    </aside>;
}